import type {
  ApiMember,
  ApiPlayer,
  ApiTransferOffer,
  FeeType,
  PeladaDetail,
  PeladaSummary,
  PlayerLinkInput,
  Role,
} from '@shared/api';
import type { PlayerProfile } from '@core/utils/rankings';

// Cliente das rotas de pelada. Concentra o tratamento de erro num lugar só, para
// que cada tela não reinvente o `catch` e acabe engolindo falha em silêncio.

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const response = await fetch(path, {
      ...init,
      headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    });

    const body: unknown = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error =
        typeof body === 'object' && body !== null && 'error' in body
          ? String((body as { error: unknown }).error)
          : 'unknown';
      return { ok: false, error };
    }

    return { ok: true, data: body as T };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export function listPeladas() {
  return request<{ peladas: PeladaSummary[] }>('/api/peladas');
}

export function createPelada(name: string, playersPerTeam: number) {
  return request<{ pelada: PeladaSummary }>('/api/peladas', {
    method: 'POST',
    body: JSON.stringify({ name, playersPerTeam }),
  });
}

export function getPelada(id: string) {
  return request<PeladaDetail>(`/api/peladas/${id}`);
}

export function listPlayers(peladaId: string) {
  return request<{ players: ApiPlayer[] }>(`/api/peladas/${peladaId}/players`);
}

export function addPlayer(
  peladaId: string,
  input: { name: string; level: number; gender?: 'M' | 'F' | null; feeType?: FeeType | null },
) {
  return request<{ player: ApiPlayer }>(`/api/peladas/${peladaId}/players`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// `reason` só viaja quando a nota muda — é o campo do log privado.
/**
 * Edita o jogador. Parcial: o servidor mescla com o que já está gravado, então
 * mandar só `active` não zera o nível.
 *
 * `reason` alimenta o log privado de alteração de nota, e só tem efeito quando
 * o nível muda de fato.
 */
export function updatePlayerRating(
  peladaId: string,
  playerId: string,
  input: {
    level?: number;
    reason?: string;
    active?: boolean;
    feeType?: FeeType | null;
    gender?: 'M' | 'F' | null;
    phone?: string | null;
    contactEmail?: string | null;
    notes?: string | null;
  },
) {
  return request<{ player: ApiPlayer }>(`/api/peladas/${peladaId}/players/${playerId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export interface RatingChangeEntry {
  id: string;
  playerId: string;
  playerName: string;
  fromLevel: number | null;
  toLevel: number;
  actorEmail: string;
  reason: string | null;
  createdAt: string;
}

export function listRatingChanges(peladaId: string) {
  return request<{ changes: RatingChangeEntry[] }>(`/api/peladas/${peladaId}/rating-changes`);
}

export interface ApiDrawTeam {
  /** Ordinal do time no sorteio (0, 1, 2…) — é ele que escolhe a cor do colete. */
  id: number;
  /** Identificador no banco. É o que a rota de partida espera receber. */
  teamId?: string;
  name: string;
  players: ApiPlayer[];
  totalStars?: number;
}

/**
 * Quem fez o sorteio.
 *
 * `null` é "não registrado", e acontece em três casos legítimos: sorteio
 * anterior a 01/08, sorteio vindo do backup do mobile, e autor que excluiu a
 * conta. A tela omite a linha nesses casos, em vez de escrever "desconhecido" —
 * dizer que o autor é desconhecido soa como informação perdida, quando o que
 * houve é que ela nunca foi gravada.
 */
export interface ApiDrawAuthor {
  userId: string;
  /** Nome do jogador vinculado à conta, ou o email quando não há vínculo. */
  name: string;
}

/**
 * Um acontecimento na vida de um sorteio: ele próprio, um rebalanceamento, um
 * "Completar", uma troca individual ou uma substituição por alguém de fora.
 *
 * `by` nulo é conta excluída, e o evento permanece: aquilo aconteceu, e um
 * registro que apaga a linha junto com o nome deixa de ser registro.
 */
export interface ApiDrawTimelineEvent {
  kind: DrawOrigin | 'swap' | 'substitute';
  at: string;
  by: ApiDrawAuthor | null;
  /** Quando foi desfeito, e por quem. `null` no caso normal. */
  undone: { at: string; by: ApiDrawAuthor | null } | null;
  /** O estado a que o evento pertence — é por ele que se abrem aqueles times. */
  drawId: string;
  /** Se este é o estado que vale hoje na família. */
  current: boolean;
  /**
   * Se dá para desfazer este acontecimento agora — o estado derivado ou a
   * troca. É o servidor que diz, pelas mesmas guardas que a rota aplica.
   */
  undoable: boolean;
  /** Presente só na troca e na substituição. */
  change: {
    changeId: string;
    /** O jogador que estava no time e saiu do lugar. */
    a: { id: string; name: string };
    /** Quem entrou no lugar dele — de outro time na troca, do elenco na substituição. */
    b: { id: string; name: string };
  } | null;
}

/**
 * Que ato produziu um estado. `draw` é o sorteio do zero, `manual` a montagem
 * à mão; `rematch`, `fill` e `restore` derivam de um estado anterior.
 */
export type DrawOrigin = 'draw' | 'manual' | 'rematch' | 'fill' | 'restore';

export interface ApiDraw {
  id: string;
  createdAt: string;
  numTeams: number;
  playersPerTeam: number;
  balanceByGender: boolean;
  createdBy: ApiDrawAuthor | null;
  /** Que ato produziu este estado. `draw` é o sorteio do zero. */
  origin: DrawOrigin;
  /** O estado anterior, quando este é derivado. É para onde o desfazer volta. */
  parentId: string | null;
  /** Quando foi desfeito, e por quem. `null` no caso normal. */
  undone: { at: string; by: ApiDrawAuthor | null } | null;
  /**
   * Se dá para desfazer agora.
   *
   * Vem do servidor de propósito: as guardas (ser o último estado vivo da
   * família, não ter partida lançada) dependem de dados que a tela não tem.
   */
  undoable: boolean;
  /**
   * Tudo que aconteceu com estes times, do mais recente para o mais antigo.
   *
   * É da FAMÍLIA inteira, e não deste estado sozinho: o sorteio original, os
   * rebalanceamentos, os "Completar", as trocas e o que foi desfeito.
   */
  timeline: ApiDrawTimelineEvent[];
  teams: ApiDrawTeam[];
  metrics: { averageStars: number; spread: number } | null;
}

export interface ApiDrawSummary {
  id: string;
  createdAt: string;
  numTeams: number;
  playersPerTeam: number;
  balanceByGender: boolean;
  createdBy: ApiDrawAuthor | null;
  /** Que ato produziu este estado. */
  origin: DrawOrigin;
  /** A raiz da família. `null` quando este registro É a raiz. */
  rootId: string | null;
  /** O estado anterior, quando este é derivado. */
  parentId: string | null;
}

export function runDraw(
  peladaId: string,
  input: {
    numTeams: number;
    playersPerTeam: number;
    balanceByGender?: boolean;
    /** Quem entra no sorteio. Ausente leva o elenco inteiro. */
    playerIds?: string[];
    /**
     * Times montados à mão: os ids de cada um, em ordem.
     *
     * ⚠️ Presente, ele MANDA — nenhum algoritmo roda, e `balanceByGender` deixa
     * de significar coisa alguma. É a única entrada do produto em que a
     * composição vem do cliente, e por isso o servidor confere jogador por
     * jogador (elenco, duplicata, capacidade) e soma as estrelas a partir do
     * banco, nunca do que chegou.
     */
    teams?: string[][];
  },
) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function listDraws(peladaId: string) {
  return request<{ draws: ApiDrawSummary[] }>(`/api/peladas/${peladaId}/draws`);
}

export function getDraw(peladaId: string, drawId: string) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws/${drawId}`);
}

/**
 * Mistura dois times do sorteio e grava o resultado como um sorteio NOVO.
 *
 * Manda os dois ordinais (`team.id` na resposta da API), e não a composição: o
 * cálculo é do servidor, então não existe pedido capaz de mover alguém para
 * onde ele não estava.
 */
export function rematchDraw(peladaId: string, drawId: string, teamA: number, teamB: number) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws/${drawId}/rematch`, {
    method: 'POST',
    body: JSON.stringify({ teamA, teamB }),
  });
}

/**
 * Completa o time incompleto com jogadores de um time cheio — o rodízio.
 *
 * Manda um ordinal só: de qual time cheio vêm os jogadores, que é o que perdeu.
 * Qual é o incompleto o servidor descobre sozinho, então não há como a tela e o
 * servidor discordarem sobre quem está de fora.
 */
export function fillDrawTeam(peladaId: string, drawId: string, source: number) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws/${drawId}/fill`, {
    method: 'POST',
    body: JSON.stringify({ source }),
  });
}

/**
 * Troca dois jogadores de time, no próprio sorteio.
 *
 * Diferente da mistura, não gera sorteio novo: é correção de uma linha, e cada
 * ajuste consumindo uma das 20 vagas do histórico o encheria de versões que
 * ninguém reabre.
 */
export function swapDrawPlayers(
  peladaId: string,
  drawId: string,
  playerA: string,
  playerB: string,
) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws/${drawId}/swap`, {
    method: 'POST',
    body: JSON.stringify({ playerA, playerB }),
  });
}

/**
 * Tira `out` do sorteio e põe `in` — alguém do elenco que não está em time
 * nenhum — na vaga dele.
 *
 * É a resposta ao meio do jogo: alguém saiu, entrou outro que não estava no
 * sorteio. Escreve no lugar, como a troca; e ao contrário dela, partida
 * lançada não a trava — o placar registrado guarda a própria escalação.
 *
 * O nível de quem entra é o que está no elenco NA HORA. Quem quiser ajustá-lo
 * antes chama `updatePlayerRating` primeiro; é assim que o ajuste passa pelo
 * log privado de nota, e é o nível ajustado que o total do time recebe.
 */
export function substituteDrawPlayer(peladaId: string, drawId: string, out: string, into: string) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws/${drawId}/substitute`, {
    method: 'POST',
    body: JSON.stringify({ out, in: into }),
  });
}

/**
 * Os times como estavam num momento da linha do tempo: o estado em si, ou —
 * com `changeId` — logo depois daquela troca ou substituição, reconstruído.
 *
 * `current` diz se aquele momento é o que está em campo agora; é o que decide
 * se "voltar para este ponto" faz sentido.
 */
export function getDrawMoment(peladaId: string, drawId: string, changeId?: string) {
  const query = changeId ? `?changeId=${encodeURIComponent(changeId)}` : '';
  return request<{ teams: ApiDrawTeam[]; current: boolean }>(
    `/api/peladas/${peladaId}/draws/${drawId}/moment${query}`,
  );
}

/**
 * Volta para um momento da linha do tempo: os times daquele instante viram um
 * estado novo (origem `restore`), filho do que vale hoje, e o jogo segue dali.
 * Nada é apagado, e a volta é desfazível como qualquer outro estado derivado.
 */
export function restoreDrawMoment(peladaId: string, drawId: string, changeId?: string) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws/${drawId}/restore`, {
    method: 'POST',
    body: JSON.stringify(changeId ? { changeId } : {}),
  });
}

/**
 * Desfaz o rebalanceamento (ou o "Completar") que produziu este sorteio, e
 * devolve o sorteio anterior — já pronto para a tela mostrar.
 *
 * O estado desfeito não some: ele sai do histórico e continua abrindo pelo link
 * direto, marcado. Apagar faria o registro do grupo mentir por omissão.
 */
export function undoDraw(peladaId: string, drawId: string) {
  return request<ApiDraw>(`/api/peladas/${peladaId}/draws/${drawId}/undo`, { method: 'POST' });
}

/**
 * Desfaz uma troca individual (os dois voltam para os times de onde saíram) ou
 * uma substituição (quem entrou sai, quem saiu volta). A linha do log fica
 * marcada como desfeita.
 *
 * Só a última troca viva pode ser desfeita, e é o servidor que diz qual é
 * (`change.undoable`).
 */
export function undoDrawChange(peladaId: string, drawId: string, changeId: string) {
  return request<ApiDraw>(
    `/api/peladas/${peladaId}/draws/${drawId}/changes/${changeId}/undo`,
    { method: 'POST' },
  );
}

export interface ApiMatch {
  id: string;
  drawRecordId: string;
  homeTeamId: string;
  awayTeamId: string;
  resultType: 'win' | 'draw';
  winnerSide: 'home' | 'away' | null;
  mvpPlayerId: string | null;
  createdAt: string;
}

export interface MatchDraft {
  homeTeamId: string;
  awayTeamId: string;
  resultType: 'win' | 'draw';
  winnerSide: 'home' | 'away' | null;
  mvpPlayerId: string | null;
  lineup: { playerId: string; side: 'home' | 'away' }[];
  goals: { playerId: string; count: number }[];
}

export function createMatch(peladaId: string, drawId: string, draft: MatchDraft) {
  return request<{ match: ApiMatch }>(`/api/peladas/${peladaId}/draws/${drawId}/matches`, {
    method: 'POST',
    body: JSON.stringify(draft),
  });
}

export function listMatches(peladaId: string, drawId: string) {
  return request<{ matches: ApiMatch[] }>(`/api/peladas/${peladaId}/draws/${drawId}/matches`);
}

export function deleteMatch(peladaId: string, matchId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/matches/${matchId}`, { method: 'DELETE' });
}

export interface RankingRow {
  id: string;
  name: string;
}

export interface Ranking {
  period: string;
  matches: number;
  players: (RankingRow & { played: number; wins: number; draws: number; losses: number; winRate: number })[];
  scorers: (RankingRow & { goals: number; matches: number; perMatch: number })[];
  mvps: (RankingRow & { count: number })[];
  attendance: (RankingRow & { attended: number; total: number; percentage: number })[];
}

export function getRanking(peladaId: string, period: string) {
  return request<Ranking>(`/api/peladas/${peladaId}/ranking?period=${period}`);
}

export function getProfile(peladaId: string, playerId: string) {
  return request<{ profile: unknown }>(`/api/peladas/${peladaId}/players/${playerId}/profile`);
}

export interface ApiSession {
  id: string;
  date: string;
  time: string | null;
  maxPlayers: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes: string | null;
}

export interface ApiRsvp {
  id: string;
  playerId: string;
  state: 'confirmed' | 'waitlisted';
  position: number;
}

export function listSessions(peladaId: string) {
  return request<{ sessions: { session: ApiSession; rsvps: ApiRsvp[] }[] }>(
    `/api/peladas/${peladaId}/sessions`,
  );
}

export function createSession(
  peladaId: string,
  input: { date: string; time: string | null; maxPlayers: number; notes: string | null },
) {
  return request<{ session: ApiSession }>(`/api/peladas/${peladaId}/sessions`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function joinSession(peladaId: string, sessionId: string, playerId: string) {
  return request<{ state: 'confirmed' | 'waitlisted' }>(
    `/api/peladas/${peladaId}/sessions/${sessionId}/rsvp`,
    { method: 'POST', body: JSON.stringify({ playerId }) },
  );
}

/**
 * Confirma vários de uma vez. A ordem do array vira a ordem da fila.
 *
 * Devolve a lista de RSVPs já atualizada — a tela não precisa reler as sessões
 * depois de cada toque.
 */
export function joinSessionBulk(peladaId: string, sessionId: string, playerIds: string[]) {
  return request<{ rsvps: ApiRsvp[] }>(
    `/api/peladas/${peladaId}/sessions/${sessionId}/rsvp/bulk`,
    { method: 'POST', body: JSON.stringify({ playerIds }) },
  );
}

export function leaveSession(peladaId: string, sessionId: string, playerId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/sessions/${sessionId}/rsvp/${playerId}`, {
    method: 'DELETE',
  });
}

export function setSessionStatus(
  peladaId: string,
  sessionId: string,
  status: 'scheduled' | 'completed' | 'cancelled',
) {
  return request<{ session: ApiSession }>(`/api/peladas/${peladaId}/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export interface ApiCharge {
  id: string;
  eventId: string | null;
  playerId: string;
  description: string;
  amountCents: number;
  status: 'paid' | 'pending';
  dueDate: string | null;
}

export interface ApiExpense {
  id: string;
  date: string | null;
  description: string;
  amountCents: number;
  status: 'paid' | 'pending';
}

export interface ApiFinanceSettings {
  pixKey: string | null;
  monthlyFeeCents: number | null;
  guestFeeCents: number | null;
  dueDay: number | null;
  rules: string | null;
}

export interface FinanceSnapshot {
  balance: {
    receivedCents: number;
    spentCents: number;
    balanceCents: number;
    pendingCents: number;
    payableCents: number;
  };
  debtors: { playerId: string; totalCents: number; charges: number }[];
  charges: ApiCharge[];
  expenses: ApiExpense[];
  events: { id: string; kind: string; date: string | null; label: string }[];
  settings: ApiFinanceSettings | null;
}

export function getFinance(peladaId: string) {
  return request<FinanceSnapshot>(`/api/peladas/${peladaId}/finance`);
}

export function splitCharge(
  peladaId: string,
  input: { totalCents: number; playerIds: string[]; description: string },
) {
  return request<{ charges: ApiCharge[] }>(`/api/peladas/${peladaId}/finance/split`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function addCharge(
  peladaId: string,
  input: { playerId: string; amountCents: number; description: string },
) {
  return request<{ charge: ApiCharge }>(`/api/peladas/${peladaId}/finance/charges`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function setChargePaid(peladaId: string, chargeId: string, paid: boolean) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/finance/charges/${chargeId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: paid ? 'paid' : 'pending' }),
  });
}

export function addExpense(
  peladaId: string,
  input: { description: string; amountCents: number; date: string | null; status: 'paid' | 'pending' },
) {
  return request<{ expense: ApiExpense }>(`/api/peladas/${peladaId}/finance/expenses`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function setExpensePaid(peladaId: string, expenseId: string, paid: boolean) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/finance/expenses/${expenseId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: paid ? 'paid' : 'pending' }),
  });
}

export function saveFinanceSettings(peladaId: string, settings: ApiFinanceSettings) {
  return request<{ settings: ApiFinanceSettings }>(`/api/peladas/${peladaId}/finance/settings`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

export function removePlayer(peladaId: string, playerId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/players/${playerId}`, {
    method: 'DELETE',
  });
}

export function listMembers(peladaId: string) {
  return request<{ members: ApiMember[] }>(`/api/peladas/${peladaId}/members`);
}

export function updateMember(
  peladaId: string,
  membershipId: string,
  // Só o papel desde 30/07: ver nota virou o cargo AUDITOR, e as flags saíram
  // do contrato — o servidor também deixou de aceitá-las.
  changes: { role: Role },
) {
  return request<{ membership: ApiMember }>(`/api/peladas/${peladaId}/members/${membershipId}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
}

/**
 * Vincula conta ↔ jogador em massa. Manda só as linhas que mudaram; membro
 * ausente da lista fica como está.
 */
export function savePlayerLinks(peladaId: string, links: PlayerLinkInput[]) {
  return request<{ linked: number }>(`/api/peladas/${peladaId}/player-links`, {
    method: 'PUT',
    body: JSON.stringify({ links }),
  });
}

export function removeMember(peladaId: string, membershipId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/members/${membershipId}`, {
    method: 'DELETE',
  });
}

export function getPlayerProfile(peladaId: string, playerId: string) {
  return request<{ profile: PlayerProfile }>(
    `/api/peladas/${peladaId}/players/${playerId}/profile`,
  );
}

/**
 * Convida um membro a assumir a titularidade.
 *
 * ⚠️ Chamava-se `transferPelada` e transferia na hora. Desde 06/09 ela apenas
 * CONVIDA: quem vira titular é quem aceita, em `acceptTransfer`. O nome mudou
 * junto com o significado de propósito — uma função chamada "transferir" que
 * não transfere é a próxima tela dizendo "pronto!" antes de estar pronto.
 */
export function offerTransfer(peladaId: string, membershipId: string) {
  return request<{ transfer: ApiTransferOffer }>(`/api/peladas/${peladaId}/transfer`, {
    method: 'POST',
    body: JSON.stringify({ membershipId }),
  });
}

export function getTransferOffer(peladaId: string) {
  return request<{ transfer: ApiTransferOffer | null }>(`/api/peladas/${peladaId}/transfer`);
}

export function acceptTransfer(peladaId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/transfer/accept`, { method: 'POST' });
}

export function declineTransfer(peladaId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/transfer/decline`, { method: 'POST' });
}

export function cancelTransfer(peladaId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}/transfer`, { method: 'DELETE' });
}

export function deletePelada(peladaId: string) {
  return request<{ ok: true }>(`/api/peladas/${peladaId}`, { method: 'DELETE' });
}

/**
 * Gera um convite. Todo mundo entra como MEMBER — o papel é do dono, depois.
 *
 * `playerId` amarra o link a um nome do elenco: quem aceita já entra vinculado
 * ao próprio histórico, sem pedir vínculo.
 */
export function createInvite(peladaId: string, playerId?: string | null) {
  return request<{
    url: string;
    invite: { role: Role; expiresAt: string; playerId: string | null; playerName: string | null };
  }>(`/api/peladas/${peladaId}/invites`, {
    method: 'POST',
    body: JSON.stringify({ playerId: playerId ?? null }),
  });
}

/** Derruba todos os links pendentes de uma vez. É a resposta a "vazou". */
export function revokePendingInvites(peladaId: string) {
  return request<{ revoked: number }>(`/api/peladas/${peladaId}/invites`, { method: 'DELETE' });
}

/**
 * O que o convite é, antes de pedir conta.
 *
 * Única chamada de convite que funciona deslogado — é para isso que ela existe.
 * Não traz quem convidou: o link é encaminhável, e o `user` só tem email.
 */
export type InvitePreview =
  | { valid: true; peladaName: string; playerName: string | null }
  | { valid: false; reason: string };

export function previewInvite(token: string) {
  return request<InvitePreview>(`/api/invites/preview?token=${encodeURIComponent(token)}`);
}

export function acceptInvite(token: string) {
  return request<{
    pelada: PeladaSummary;
    role: Role;
    player: { id: string; name: string } | null;
    /** O convite trazia um nome, mas ele já tinha dono quando o aceite chegou. */
    linkMissed: boolean;
  }>('/api/invites/accept', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export function importLocal(input: {
  name: string;
  playersPerTeam: number;
  players: { name: string; level: number; gender?: 'M' | 'F' | null }[];
}) {
  return request<{ pelada: PeladaSummary; imported: number }>('/api/peladas/import-local', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
